// src/App.js
import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

import ScrollToTop from './components/ScrollToTop';

import AuthShell from './components/auth/AuthShell';
import LoginScreen from './components/auth/LoginScreen';
import SignupScreen from './pages/SignupScreen';
import LandingPage from './pages/LandingPage';
import EditAccount from './components/EditAccount';
import StudentDashboard from './components/StudentDashboard';
import StudentDashboardShell from './components/student/StudentDashboardShell';
import StudentPreviewRoute from './components/student/StudentPreviewRoute';
import StudentVideosPage from './components/student/StudentVideosPage';
import VideoUploadPage from './components/student/VideoUploadPage';
import VideoFeedbackPage from './components/student/VideoFeedbackPage';
// Pass 75 (§13) — MessagesPage retired (no longer mounted); the canonical Inbox (InboxPanel) is the ONE messaging surface.
import InstructorDashboardShell from './components/instructor/InstructorDashboardShell';
import InstructorFeedbackCenter from './components/InstructorFeedbackCenter'; // ✅ NEW
import AdminDashboard from './components/AdminDashboard';
import PlatformQaPreview from './components/qa/PlatformQaPreview';
import CompleteProfile from './pages/CompleteProfile';
import AuthActionPage from './pages/AuthActionPage';
import ApplyInstructor from './pages/ApplyInstructor';
import ReferralPage from './pages/ReferralPage';
import InboxPanel from './components/inbox/InboxPanel';
import GlobalHeader from './components/shared/GlobalHeader';
import { resolveDashboardHome } from './routes';
import AccountSettings from './pages/AccountSettings';
import CoachingProfilePage from './pages/CoachingProfilePage';
import CorsTest from './components/CorsTest';
import VideoEditorPage from './pages/VideoEditorPage';
import MySkillsPage from './pages/MySkillsPage';
import StudentLessonsPage from './pages/StudentLessonsPage';
import StudentLessonDetailPage from './pages/StudentLessonDetailPage';
import OrganizationDashboardShell from './components/instructor/OrganizationDashboardShell';
import AcceptOrganizationInvitePage from './pages/AcceptOrganizationInvitePage';
import InstructorLessonBuilderPage from './pages/InstructorLessonBuilderPage';
import InstructorQuickLessonLogPage from './pages/InstructorQuickLessonLogPage';
import PlatformAdminContentPacksPage from './pages/PlatformAdminContentPacksPage';
import PlatformAdminBillingPage from './pages/PlatformAdminBillingPage';
import DrillTemplateManagementPage from './pages/DrillTemplateManagementPage';
import DiscoveryPage from './pages/DiscoveryPage';
import PublicOrganizationProfilePage from './pages/PublicOrganizationProfilePage';
import PublicInstructorProfilePage from './pages/PublicInstructorProfilePage';
import InstructorPublicProfilePage from './pages/InstructorPublicProfilePage';
import RequesterBookingsPage from './pages/RequesterBookingsPage';
import ClientProfilePage from './pages/ClientProfilePage';
import RetentionQueuePage from './pages/RetentionQueuePage';
import RetentionReportingPage from './pages/RetentionReportingPage';
import InstructorSchedulePage from './pages/InstructorSchedulePage';
import StudentPackageSelectionPage from './pages/StudentPackageSelectionPage';
import {
  canAccessOrganizationDashboard,
  canAccessDrillTemplateManagement,
  canManageContentPack,
  canManageSubscriptionPlans,
  canEnterInstructorWorkspace,
  canEnterOrganizationWorkspace,
  canPreviewStudentWorkspace,
} from './utils/platformAccess';
import { getOrganizationMemberships } from './utils/organizationContext';

// Pass 52 §7 / Pass 58F6 — the legacy instructor video-queue dashboard is lazy so fast paths render
// without downloading it (it only backs the instructor Video Queue tab). NOTE: the ~3450-line legacy
// org dashboard page is NO LONGER mounted anywhere — Pass 58F6 detached it from the Organisation
// Settings tab. It is preserved on disk (see docs/LEGACY_CAPABILITY_INVENTORY.md), not imported here.
// Pass 71 — the legacy InstructorDashboard is likewise DETACHED: the Video Queue tab now mounts the focused
// InstructorVideoQueue, so the giant dashboard is no longer imported here (preserved on disk, see inventory).

function ProtectedRoute({ children }) {
  const { currentUser } = useAuth() || {};
  const location = useLocation();
  if (currentUser) return children;

  const redirectPath = `${location.pathname}${location.search}`;
  return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} />;
}

function RequireProfileCompletion({ children }) {
  const { userData } = useAuth() || {};

  if (userData === null) return <div>Loading profile...</div>;

  // Pass 58G4 — canonical STAFF (Operations and/or Instructor entitlement) must NOT be forced through the
  // student age/country onboarding. A freshly-activated ops/instructor member has no legacy role and no
  // age/country, but their canonical membership entitles them to their staff workspace directly. Genuine
  // students (no staff entitlement) still complete their profile. Derive from entitlement, not a role string.
  if (canEnterOrganizationWorkspace(userData || {}) || canEnterInstructorWorkspace(userData || {})) {
    return children;
  }

  // Legacy non-student roles also skip student profile completion (un-migrated org/admin accounts).
  if (userData?.role === 'admin' || userData?.role === 'organization') {
    return children;
  }

  if (!userData?.name || !userData?.age || !userData?.country) {
    return <Navigate to="/complete-profile" />;
  }

  return children;
}

function RequireOrganizationAccess({ children }) {
  const { userData } = useAuth() || {};

  if (userData === null) return <div>Loading organization access...</div>;
  if (canAccessOrganizationDashboard(userData || {})) return children;
  return <Navigate to="/dashboard" />;
}

function RequirePlatformAdmin({ children }) {
  const { userData } = useAuth() || {};

  if (userData === null) return <div>Loading platform access...</div>;
  if (canManageContentPack(userData || {}) || canManageSubscriptionPlans(userData || {})) return children;
  return <Navigate to="/dashboard" />;
}

// Pass 82D — Student Preview is available to org owners/admins (or super-admin) only; never a plain instructor/student.
function RequireStudentPreviewAccess({ children }) {
  const { userData } = useAuth() || {};
  if (userData === null) return <div>Loading…</div>;
  if (canPreviewStudentWorkspace(userData || {}, '')) return children;
  return <Navigate to="/dashboard" />;
}

function RequireContentManagementAccess({ children }) {
  const { userData } = useAuth() || {};

  if (userData === null) return <div>Loading content access...</div>;
  if (canAccessDrillTemplateManagement(userData || {})) return children;
  return <Navigate to="/dashboard" />;
}

function RequireInstructorAccess({ children }) {
  const { userData } = useAuth() || {};

  if (userData === null) return <div>Loading instructor access...</div>;
  // Pass 58F — the instructor workspace requires a CANONICAL teaching relationship
  // (isTeachingInstructor), NOT merely a legacy users.role string. John (organization_owner,
  // isTeachingInstructor=false) must NOT reach instructor routes even by typing the URL. The legacy
  // role fallback applies ONLY to un-migrated accounts that have no canonical membership at all.
  if (canEnterInstructorWorkspace(userData || {})) return children;
  const hasCanonicalMembership = getOrganizationMemberships(userData || {}).length > 0;
  if (!hasCanonicalMembership &&
      (userData?.role === 'instructor' || userData?.role === 'organization' || userData?.role === 'admin')) {
    return children;
  }
  return <Navigate to="/dashboard" />;
}

// Pass 44 — legacy → canonical redirects (preserve params + query, redirect once).
function LegacyRedirect({ to }) {
  const params = useParams();
  const location = useLocation();
  let target = to;
  Object.entries(params).forEach(([k, v]) => { target = target.replace(`:${k}`, v); });
  return <Navigate to={`${target}${location.search}`} replace />;
}
// /organization → /org/:organizationId (resolve the user's org).
function OrgHomeRedirect() {
  const { userData } = useAuth() || {};
  const location = useLocation();
  const orgId = userData?.orgId || userData?.organizationId || 'default';
  return <Navigate to={`/org/${orgId}${location.search}`} replace />;
}
// Pass 58F3 — the ONE canonical Inbox entry. An org member gets the Inbox INSIDE their org workspace
// shell (so the SAS nav persists — /org/:orgId/inbox); everyone else gets the standalone panel under the
// global header. Preserves ?tab.
function InboxRoute() {
  const { userData } = useAuth() || {};
  const location = useLocation();
  const orgId = userData?.orgId || userData?.organizationId || '';
  if (orgId && canAccessOrganizationDashboard(userData || {})) {
    return <Navigate to={`/org/${orgId}/inbox${location.search}`} replace />;
  }
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px 80px' }}>
      <InboxPanel />
    </div>
  );
}
// Legacy notification routes (/notifications, /notification-center, /notifications-old) collapse to the
// ONE canonical Inbox, preserving any ?tab/query so deep-links keep working.
function LegacyInboxRedirect() {
  const location = useLocation();
  return <Navigate to={`/inbox${location.search}`} replace />;
}

// Pass 75 (§13) — the duplicate MessagesPage is retired. /me/messages and /messages redirect to the ONE
// canonical Inbox's Messages tab, preserving a ?thread=<id> deep-link.
function MessagesRedirect() {
  const location = useLocation();
  const incoming = new URLSearchParams(location.search);
  const out = new URLSearchParams();
  out.set('tab', 'messages');
  const thread = incoming.get('thread');
  if (thread) out.set('thread', thread);
  return <Navigate to={`/inbox?${out.toString()}`} replace />;
}

// Legacy /dashboard resolver → canonical workspace home (renders no shell).
function DashboardResolver() {
  const { userData, viewMode } = useAuth() || {};
  if (userData === null) return <div className="text-center mt-10">Loading…</div>;
  return <Navigate to={resolveDashboardHome(userData, viewMode)} replace />;
}

// Pass hosting/UX — the production sign-in: a modern two-panel experience (brand/story + auth card).
// No stacked signup form, no Facebook, no duplicated headings.
function LoginPage() {
  return (
    <AuthShell>
      <LoginScreen />
    </AuthShell>
  );
}

// Dev utility — run window.__seedOrgAccount() in browser console to create org test account
if (process.env.NODE_ENV !== 'production') { import('./utils/seedOrgAccount').catch(() => {}); }

function App() {
  const auth = useAuth();
  if (!auth) return <div>Loading auth context...</div>;

  const { currentUser, loading } = auth;
  if (loading) return <div className="text-center mt-10">Loading...</div>;

  return (
    <>
      {currentUser && <GlobalHeader />}
      <ScrollToTop />

      <Routes>
        {/* Public front-door. Authenticated users go straight to their canonical dashboard/workspace. */}
        <Route path="/" element={!currentUser ? <LandingPage /> : <Navigate to="/dashboard" replace />} />

        <Route
          path="/login"
          element={!currentUser ? <LoginPage /> : <Navigate to={new URLSearchParams(window.location.search).get('redirect') || '/dashboard'} />}
        />

        {/* Separate, minimal public sign-up (auth essentials only → /complete-profile onboarding). */}
        <Route path="/signup" element={!currentUser ? <SignupScreen /> : <Navigate to="/dashboard" replace />} />

        {/* Pass 88.3 §4 — branded Firebase email-action handler (password reset). PUBLIC (signed-out reset flow),
            and signed-in-safe; it must NOT bounce to /dashboard, so it is intentionally outside the login redirect. */}
        <Route path="/auth/action" element={<AuthActionPage />} />

        <Route path="/discover" element={<DiscoveryPage />} />
        <Route path="/orgs/:publicSlug" element={<PublicOrganizationProfilePage />} />
        <Route path="/instructors/:publicSlug" element={<PublicInstructorProfilePage />} />

        <Route
          path="/complete-profile"
          element={
            <ProtectedRoute>
              <CompleteProfile />
            </ProtectedRoute>
          }
        />

        {/* Pass 44c: /dashboard is now a LEGACY RESOLVER only — it renders no shell,
            it redirects to the canonical workspace home. New code must not link here. */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardResolver /></ProtectedRoute>} />
        {/* Pass 87.1 — platform QA/demo preview. The component self-guards on canUsePlatformQaPreview (platform
            super-admin only) and redirects ordinary org users, so typing this URL grants nothing. */}
        <Route path="/qa/preview" element={<ProtectedRoute><PlatformQaPreview /></ProtectedRoute>} />

        {/* Canonical Personal + Instructor routes */}
        {/* Pass 76/78 — ONE canonical student progress/skills surface. /me/progress renders the review-derived
            reader (MySkillsPage, top-level videos + lessonInstances). The legacy quickCapture-cache progression
            stack (StudentProgressDashboard + useStudentProgression) was removed in Pass 78. /my-skills redirects
            here (below). */}
        <Route path="/me/progress" element={<ProtectedRoute><RequireProfileCompletion><MySkillsPage /></RequireProfileCompletion></ProtectedRoute>} />
        <Route path="/me/videos" element={<ProtectedRoute><RequireProfileCompletion><StudentVideosPage /></RequireProfileCompletion></ProtectedRoute>} />
        {/* Pass 85.1A.1 — the student product is UNIFIED: `/me` (+ videos/lessons/progress/upload) IS the canonical
            participant-scoped surface, so the former separate `/me/coaching` dashboard is now just an alias → `/me`. */}
        <Route path="/me/coaching" element={<ProtectedRoute><LegacyRedirect to="/me" /></ProtectedRoute>} />
        <Route path="/me/messages" element={<ProtectedRoute><MessagesRedirect /></ProtectedRoute>} />
        <Route path="/instructor/schedule" element={<ProtectedRoute><RequireInstructorAccess><InstructorSchedulePage /></RequireInstructorAccess></ProtectedRoute>} />
        {/* Legacy → canonical */}
        <Route path="/dashboard/progress" element={<ProtectedRoute><LegacyRedirect to="/me/progress" /></ProtectedRoute>} />
        <Route path="/dashboard/videos" element={<ProtectedRoute><LegacyRedirect to="/me/videos" /></ProtectedRoute>} />
        <Route path="/schedule" element={<ProtectedRoute><LegacyRedirect to="/instructor/schedule" /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesRedirect /></ProtectedRoute>} />

        <Route
          path="/dashboard/packages"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <StudentPackageSelectionPage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route path="/me/upload" element={<ProtectedRoute><RequireProfileCompletion><VideoUploadPage /></RequireProfileCompletion></ProtectedRoute>} />
        <Route path="/upload" element={<ProtectedRoute><LegacyRedirect to="/me/upload" /></ProtectedRoute>} />

        <Route
          path="/edit-account"
          element={
            <ProtectedRoute>
              <EditAccount />
            </ProtectedRoute>
          }
        />

        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          }
        />

        {/* Pass 44: workspace home destinations. /me = Personal (renders regardless
            of role so an org/instructor operator still has their own coaching space). */}
        <Route
          path="/me"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <StudentDashboardShell legacyDashboard={<StudentDashboard />} />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />
        {/* Pass 82D — authorized-staff Student Preview (read-only). A SEPARATE viewer context; never overloads /me. */}
        <Route
          path="/student-preview"
          element={
            <ProtectedRoute>
              <RequireStudentPreviewAccess>
                <StudentPreviewRoute />
              </RequireStudentPreviewAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/me/coaching-profile"
          element={
            <ProtectedRoute>
              <CoachingProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/instructor"
          element={
            <ProtectedRoute>
              <RequireInstructorAccess>
                <InstructorDashboardShell />
              </RequireInstructorAccess>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <RequirePlatformAdmin>
                <AdminDashboard />
              </RequirePlatformAdmin>
            </ProtectedRoute>
          }
        />

        <Route
          path="/referral"
          element={
            <ProtectedRoute>
              <ReferralPage />
            </ProtectedRoute>
          }
        />

        {/* Canonical Inbox (Notifications + Messages). Org members are routed into their workspace shell. */}
        <Route path="/inbox" element={<ProtectedRoute><InboxRoute /></ProtectedRoute>} />
        {/* Legacy notification centers → the ONE canonical Inbox. */}
        <Route path="/notifications" element={<ProtectedRoute><LegacyInboxRedirect /></ProtectedRoute>} />

        <Route
          path="/my-bookings"
          element={
            <ProtectedRoute>
              <RequesterBookingsPage />
            </ProtectedRoute>
          }
        />

        {/* Pass 79 — CANONICAL clientId-native CRM route: org context + commercial clientId are BOTH explicit. Works
            for a commercial customer with NO Auth account. `:clientId` is a commercial identity — never a users/{uid}
            lookup and never reinterpreted as an Auth uid. */}
        <Route
          path="/org/:organizationId/clients/:clientId"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <ClientProfilePage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        {/* LEGACY Auth-backed CRM route — `:clientUserId` stays an Auth uid and is NEVER reinterpreted as a clientId. */}
        <Route
          path="/clients/:clientUserId"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <ClientProfilePage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/retention"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RetentionQueuePage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/retention/reporting"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RetentionReportingPage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/retention/reporting/:reportView"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RetentionReportingPage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/retention/:queueType"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RetentionQueuePage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route path="/notifications-old" element={<ProtectedRoute><LegacyInboxRedirect /></ProtectedRoute>} />
        <Route path="/notification-center" element={<ProtectedRoute><LegacyInboxRedirect /></ProtectedRoute>} />

        <Route
          path="/cors-test"
          element={
            <ProtectedRoute>
              <CorsTest />
            </ProtectedRoute>
          }
        />

        <Route
          path="/apply-instructor"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <ApplyInstructor />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/video/:videoId/editor"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <VideoEditorPage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reviews/:videoId"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <VideoFeedbackPage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        {/* Pass 76 — /my-skills is a LEGACY ALIAS → canonical /me/progress (LegacyRedirect preserves the
            sport/skill query string). New links must use buildMySkillsPath() (→ /me/progress). */}
        <Route path="/my-skills" element={<ProtectedRoute><LegacyRedirect to="/me/progress" /></ProtectedRoute>} />

        {/* Canonical Organisation workspace: /org/:organizationId[/:tab] */}
        <Route
          path="/org/:organizationId"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RequireOrganizationAccess>
                  <OrganizationDashboardShell />
                </RequireOrganizationAccess>
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />
        <Route
          path="/org/:organizationId/:tab"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RequireOrganizationAccess>
                  <OrganizationDashboardShell />
                </RequireOrganizationAccess>
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />
        {/* Legacy → canonical */}
        <Route path="/organization" element={<ProtectedRoute><OrgHomeRedirect /></ProtectedRoute>} />

        <Route
          path="/invite/:inviteCode"
          element={<AcceptOrganizationInvitePage />}
        />

        {/* Canonical Admin workspace */}
        <Route path="/admin/content" element={<ProtectedRoute><RequirePlatformAdmin><PlatformAdminContentPacksPage /></RequirePlatformAdmin></ProtectedRoute>} />
        <Route path="/admin/billing" element={<ProtectedRoute><RequirePlatformAdmin><PlatformAdminBillingPage /></RequirePlatformAdmin></ProtectedRoute>} />
        <Route path="/platform/content-packs" element={<ProtectedRoute><LegacyRedirect to="/admin/content" /></ProtectedRoute>} />
        <Route path="/platform/billing" element={<ProtectedRoute><LegacyRedirect to="/admin/billing" /></ProtectedRoute>} />

        <Route
          path="/content/drills"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RequireContentManagementAccess>
                  <DrillTemplateManagementPage />
                </RequireContentManagementAccess>
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/content/drills/new"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RequireContentManagementAccess>
                  <DrillTemplateManagementPage />
                </RequireContentManagementAccess>
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/content/drills/:templateId/edit"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RequireContentManagementAccess>
                  <DrillTemplateManagementPage />
                </RequireContentManagementAccess>
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route path="/me/lessons" element={<ProtectedRoute><RequireProfileCompletion><StudentLessonsPage /></RequireProfileCompletion></ProtectedRoute>} />
        <Route
          path="/lessons"
          element={
            <ProtectedRoute>
              <LegacyRedirect to="/me/lessons" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/lessons/:lessonId"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <StudentLessonDetailPage />
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/instructor/lesson-builder"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RequireInstructorAccess>
                  <InstructorLessonBuilderPage />
                </RequireInstructorAccess>
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/instructor/public-profile"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RequireInstructorAccess>
                  <InstructorPublicProfilePage />
                </RequireInstructorAccess>
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        <Route
          path="/instructor/quick-lesson-log"
          element={
            <ProtectedRoute>
              <RequireProfileCompletion>
                <RequireInstructorAccess>
                  <InstructorQuickLessonLogPage />
                </RequireInstructorAccess>
              </RequireProfileCompletion>
            </ProtectedRoute>
          }
        />

        {/* ✅ NEW: Instructor Feedback Center route */}
        <Route path="/instructor/feedback" element={<ProtectedRoute><RequireInstructorAccess><InstructorFeedbackCenter /></RequireInstructorAccess></ProtectedRoute>} />
        <Route path="/instructor-feedback-center" element={<ProtectedRoute><LegacyRedirect to="/instructor/feedback" /></ProtectedRoute>} />

        {/* Catch-all fallback */}
        <Route
          path="*"
          element={<Navigate to={currentUser ? '/dashboard' : '/login'} />}
        />
      </Routes>
    </>
  );
}

export default App;
